import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Table,
  Badge,
  Group,
  Button,
  Stack,
  Select,
  Pagination,
  ActionIcon,
  Tooltip,
  Modal,
  Alert,
  Anchor,
} from '@mantine/core';
import {
  IconPlus,
  IconEye,
  IconEdit,
  IconTrash,
  IconDownload,
  IconAlertCircle,
} from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSingleSourceForms, useDeleteSingleSourceForm } from '../hooks/useSingleSource';
import { SingleSourceForm } from '../../../api/singleSource';
import { useAuth } from '../../../auth/AuthContext';
import { canEditSingleSourceForm } from '../permissions';

const statusColors: Record<string, string> = {
  DRAFT: 'gray',
  SUBMITTED: 'blue',
  UNDER_REVIEW: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
  AWARDED: 'purple',
};

export function SingleSourceList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<SingleSourceForm | null>(null);

  const { data, isLoading, error } = useSingleSourceForms({
    status: statusFilter || undefined,
    page,
    limit: 10,
  });

  const deleteForm = useDeleteSingleSourceForm();

  const handleDelete = (form: SingleSourceForm) => {
    setFormToDelete(form);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (formToDelete) {
      deleteForm.mutate(formToDelete.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setFormToDelete(null);
        },
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MVR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          variant="light"
        >
          Failed to load single source forms. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Single Source Procurement</Title>
            <Text c="dimmed" size="sm">
              Manage single source procurement forms and submissions
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/single-source/new')}
          >
            Create New Form
          </Button>
        </Group>

        <Card shadow="sm" p="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Select
                placeholder="Filter by status"
                data={[
                  { value: '', label: 'All Statuses' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'SUBMITTED', label: 'Submitted' },
                  { value: 'UNDER_REVIEW', label: 'Under Review' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'REJECTED', label: 'Rejected' },
                  { value: 'AWARDED', label: 'Awarded' },
                ]}
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value || '');
                  setPage(1);
                }}
                w={200}
              />
            </Group>

            {isLoading ? (
              <Text>Loading...</Text>
            ) : data?.forms && data.forms.length > 0 ? (
              <>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Form Number</Table.Th>
                      <Table.Th>Vendor</Table.Th>
                      <Table.Th>Proposed Cost</Table.Th>
                      <Table.Th>Duration</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Meeting</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.forms.map((form) => (
                      <Table.Tr key={form.id}>
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={`/single-source/${form.id}`}
                            fw={500}
                          >
                            {form.formNumber}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <Text>{form.vendorName}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text>{formatCurrency(form.proposedCost)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text>{form.proposedDuration} days</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={statusColors[form.status]} size="sm">
                            {form.status.replace('_', ' ')}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatDate(form.createdAt)}</Text>
                        </Table.Td>
                        <Table.Td>
                          {form.meeting ? (
                            <Text size="sm">{form.meeting.title}</Text>
                          ) : (
                            <Text size="sm" c="dimmed">Not assigned</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="View Details">
                              <ActionIcon
                                variant="light"
                                size="sm"
                                onClick={() => navigate(`/single-source/${form.id}`)}
                              >
                                <IconEye size={14} />
                              </ActionIcon>
                            </Tooltip>
                            {canEditSingleSourceForm(form, user) && (
                              <Tooltip label="Edit">
                                <ActionIcon
                                  variant="light"
                                  size="sm"
                                  onClick={() => navigate(`/single-source/${form.id}/edit`)}
                                >
                                  <IconEdit size={14} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            <Tooltip label="Download">
                              <ActionIcon
                                variant="light"
                                size="sm"
                                onClick={() => navigate(`/single-source/${form.id}/download`)}
                              >
                                <IconDownload size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="light"
                                color="red"
                                size="sm"
                                onClick={() => handleDelete(form)}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                {data.pagination.pages > 1 && (
                  <Group justify="center" mt="md">
                    <Pagination
                      value={page}
                      onChange={setPage}
                      total={data.pagination.pages}
                    />
                  </Group>
                )}
              </>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No single source forms found. Create your first form to get started.
              </Text>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Single Source Form"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the single source form{' '}
            <Text component="span" fw={500}>
              {formToDelete?.formNumber}
            </Text>
            ? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteForm.isPending}
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
